import React, { useState } from 'react';
import { Phone, Mail, MapPin, Zap, Shield, Clock, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    street: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/inquiries`, {
        name: formData.name,
        phone: formData.phone,
        city: formData.city,
        street: formData.street,
        message: formData.message
      });
      
      toast.success('Děkujeme! Vaši poptávku jsme obdrželi. Brzy se vám ozveme.');
      setFormData({ name: '', phone: '', city: '', street: '', message: '' });
    } catch (error) {
      toast.error('Nepodařilo se odeslat poptávku. Zkuste to prosím znovu.');
      console.error('Error submitting inquiry:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const services = [
    {
      title: 'Kompletní elektroinstalace',
      description: 'Profesionální instalace elektrických rozvodů v novostavbách i rekonstrukcích.',
      icon: Zap
    },
    {
      title: 'Rekonstrukce elektro rozvodů',
      description: 'Modernizace starých rozvodů podle platných norem a bezpečnostních předpisů.',
      icon: Shield
    },
    {
      title: 'Opravy a poruchy',
      description: 'Rychlá pomoc při poruchách a závadách. Dostupný i pro akutní případy.',
      icon: Clock
    },
    {
      title: 'Výměna jističů a rozvaděčů',
      description: 'Odborná výměna a modernizace rozvaděčů včetně instalace proudových chráničů.',
      icon: CheckCircle
    },
    {
      title: 'Montáž osvětlení',
      description: 'Instalace interiérového i exteriérového osvětlení, LED pásky, bodovky.',
      icon: Zap
    },
    {
      title: 'Revize elektroinstalací',
      description: 'Pravidelné i mimořádné revize dle ČSN, vystavení revizní zprávy.',
      icon: Shield
    }
  ];

  const benefits = [
    {
      title: 'Individuální přístup',
      description: 'Každému zákazníkovi věnuji plnou pozornost a přizpůsobuji řešení jeho potřebám.'
    },
    {
      title: 'Rychlé jednání',
      description: 'Flexibilní termíny, rychlá reakce na poptávku. Akutní případy řeším přednostně.'
    },
    {
      title: 'Férové ceny',
      description: 'Transparentní cenová nabídka bez skrytých poplatků. Cena dohodnutá předem.'
    },
    {
      title: 'Zkušenosti a pečlivost',
      description: 'Více než 10 let praxe v oboru. Každou práci provádím s důrazem na detail.'
    },
    {
      title: 'Práce dle platných norem',
      description: 'Veškeré práce provádím v souladu s platnými českými normami a předpisy.'
    }
  ];

  const testimonials = [
    {
      name: 'Petr Novák',
      location: 'Rychnov nad Kněžnou',
      text: 'Výměna rozvaděče proběhla rychle a profesionálně. Pan Čejpa je spolehlivý a cenově férový.',
      rating: 5
    },
    {
      name: 'Marie Svobodová',
      location: 'Náchod',
      text: 'Rekonstrukce elektřiny v celém domě. Práce odvedena precizně, vše funguje perfektně. Mohu jen doporučit!',
      rating: 5
    },
    {
      name: 'Jan Dvořák',
      location: 'Hradec Králové',
      text: 'Rychlá reakce při poruše, oprava na počkání. Ochotný a profesionální přístup. Děkuji!',
      rating: 5
    }
  ];

  const areas = [
    'Rychnov nad Kněžnou a okolí',
    'Náchod a okolí',
    'Hradec Králové a okolí',
    'Možnost dojezdu dle domluvy'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Logo with Popover */}
              <div className="relative group">
                <Zap className="w-8 h-8 text-blue-900 cursor-pointer transition-transform hover:scale-110" />
                
                {/* Hover Popover with Ivo's Photo */}
                <div className="absolute top-full left-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                  {/* Ivo's Photo Section */}
                  <div className="relative h-48 bg-gradient-to-br from-blue-900 to-blue-700">
                    <img 
                      src="/ivo-work.png" 
                      alt="Ivo Čejpa - profesionální elektrikář"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <h4 className="font-bold text-white text-lg">Ivo Čejpa</h4>
                      <p className="text-sm text-blue-100">Váš spolehlivý elektrikář</p>
                    </div>
                  </div>
                  
                  {/* FAQ Content */}
                  <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-700 font-medium text-center">
                      💡 Mám pro vás rychlé řešení!
                    </p>
                    
                    {/* Quick FAQ Links */}
                    <div className="space-y-2">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700 mb-1">📍 V jakých oblastech pracuji?</p>
                        <a 
                          href="#oblasti" 
                          className="text-xs text-blue-900 font-semibold hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            document.querySelector('#oblasti')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                        >
                          → Zde máte odpověď
                        </a>
                      </div>
                      
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700 mb-1">⚡ Jaké služby nabízím?</p>
                        <a 
                          href="#services" 
                          className="text-xs text-blue-900 font-semibold hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                        >
                          → Zde máte odpověď
                        </a>
                      </div>
                      
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700 mb-1">✅ Proč si vybrat právě mě?</p>
                        <a 
                          href="#proc-si-vybrat" 
                          className="text-xs text-blue-900 font-semibold hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            document.querySelector('#proc-si-vybrat')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                        >
                          → Zde máte odpověď
                        </a>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-sm text-blue-900 font-semibold mb-2 text-center">
                        🚀 Potřebujete pomoc? Volejte!
                      </p>
                      <a href="tel:775280542">
                        <button className="w-full bg-blue-900 hover:bg-blue-800 text-white py-2 px-4 rounded-lg font-semibold text-sm transition-colors">
                          📞 775 280 542
                        </button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h1 className="text-xl font-bold text-blue-900">ElektroIvo</h1>
                <p className="text-sm text-gray-600">Ivo Čejpa</p>
              </div>
            </div>
            <a href="tel:775280542">
              <Button className="bg-blue-900 hover:bg-blue-800 text-white">
                <Phone className="w-4 h-4 mr-2" />
                775 280 542
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-blue-50 to-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6 leading-tight">
                Spolehlivý elektrikář pro Rychnov, Náchod a Hradec Králové
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Elektroinstalace, opravy a revize rychle a kvalitně.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:775280542">
                  <Button size="lg" className="bg-blue-900 hover:bg-blue-800 text-white w-full sm:w-auto">
                    <Phone className="w-5 h-5 mr-2" />
                    Zavolat nyní
                  </Button>
                </a>
                <a href="#contact">
                  <Button size="lg" variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-50 w-full sm:w-auto">
                    Nezávazná poptávka
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>10+ let praxe</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>Práce dle norem</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/ivo-work.png"
                  alt="Ivo Čejpa - profesionální elektrikář při práci"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-blue-900 text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">10+</div>
                <div className="text-sm">Let zkušeností</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              Nabízené služby
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Komplexní elektrikářské služby pro domácnosti i firmy v oblasti Rychnov nad Kněžnou, Náchod a Hradec Králové
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-blue-900" />
                    </div>
                    <h4 className="text-xl font-semibold text-blue-900 mb-2">
                      {service.title}
                    </h4>
                    <p className="text-gray-600">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="proc-si-vybrat" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              Proč si vybrat mě
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Spolehlivost, profesionalita a férový přístup ke každému zákazníkovi
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-blue-900 mb-2">
                  {benefit.title}
                </h4>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              Reference zákazníků
            </h3>
            <p className="text-lg text-gray-600">
              Co říkají spokojení zákazníci
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-gray-200">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">
                    "{testimonial.text}"
                  </p>
                  <div>
                    <p className="font-semibold text-blue-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section - Naše realizace */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              Naše realizace
            </h3>
            <p className="text-lg text-gray-600">
              Ukázky našich prací - profesionální instalace a modernizace
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <img 
                src="https://images.unsplash.com/photo-1544724569-5f546fd6f2b5"
                alt="Moderní rozvaděč po instalaci"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-semibold">Nový rozvaděč</p>
                <p className="text-gray-200 text-sm">Modernizace elektro rozvodů</p>
              </div>
            </div>
            
            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <img 
                src="https://images.unsplash.com/photo-1576446470246-499c738d1c8e"
                alt="Čistá instalace jističů"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-semibold">Kompletní instalace</p>
                <p className="text-gray-200 text-sm">Novostavba RD</p>
              </div>
            </div>
            
            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <img 
                src="https://images.pexels.com/photos/7541342/pexels-photo-7541342.jpeg"
                alt="Profesionální zapojení rozvaděče"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-semibold">Precizní zapojení</p>
                <p className="text-gray-200 text-sm">Rekonstrukce bytu</p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <img 
                src="https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg"
                alt="Komplexní elektro systém"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-semibold">Složitý systém</p>
                <p className="text-gray-200 text-sm">Komerční objekt</p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <img 
                src="https://images.unsplash.com/photo-1635335874521-7987db781153"
                alt="Detailní práce na instalaci"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-semibold">Detailní práce</p>
                <p className="text-gray-200 text-sm">Přípojková skříň</p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <img 
                src="https://images.pexels.com/photos/257736/pexels-photo-257736.jpeg"
                alt="Práce elektrikáře na rozvaděči"
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-semibold">Profesionální přístup</p>
                <p className="text-gray-200 text-sm">Revize a údržba</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section id="oblasti" className="py-16 md:py-24 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Oblast působení
            </h3>
            <p className="text-lg text-blue-100">
              Poskytujeme služby v těchto lokalitách
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {areas.map((area, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <MapPin className="w-8 h-8 mx-auto mb-3" />
                <p className="font-medium">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                Kontakt
              </h3>
              <p className="text-lg text-gray-600">
                Ozvěte se ještě dnes
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xl font-semibold text-blue-900 mb-6">
                  Kontaktní údaje
                </h4>
                <div className="space-y-4">
                  <a href="tel:775280542" className="flex items-center gap-4 p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6 text-blue-900" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Telefon</p>
                      <p className="font-semibold text-blue-900">775 280 542</p>
                    </div>
                  </a>
                  <a href="mailto:cejpa.ivo@seznam.cz" className="flex items-center gap-4 p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-blue-900" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold text-blue-900">cejpa.ivo@seznam.cz</p>
                    </div>
                  </a>
                </div>
                <div className="mt-6 p-6 bg-blue-900 text-white rounded-lg">
                  <h5 className="font-semibold mb-2">ElektroIvo - Ivo Čejpa</h5>
                  <p className="text-sm text-blue-100">
                    Elektrikář s více než 10 lety zkušeností v oboru. Specializuji se na kompletní elektroinstalace, revize a opravy pro domácnosti i firmy v oblasti východních Čech.
                  </p>
                </div>
              </div>
              <div>
                <h4 className="text-xl font-semibold text-blue-900 mb-6">
                  Nezávazná poptávka
                </h4>
                <Card>
                  <CardContent className="p-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Jméno a příjmení
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Vaše jméno"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Telefon
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Váš telefon"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                            Město
                          </label>
                          <Input
                            id="city"
                            name="city"
                            type="text"
                            required
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="Např. Rychnov nad Kněžnou"
                          />
                        </div>
                        <div>
                          <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-2">
                            Ulice
                          </label>
                          <Input
                            id="street"
                            name="street"
                            type="text"
                            required
                            value={formData.street}
                            onChange={handleChange}
                            placeholder="Ulice a číslo popisné"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                          Popis zakázky
                        </label>
                        <Textarea
                          id="message"
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Popište prosím, co potřebujete..."
                          rows={4}
                        />
                      </div>
                      <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-800 text-white">
                        Odeslat poptávku
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-900 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Zap className="w-6 h-6" />
              <span className="font-semibold text-lg">ElektroIvo</span>
            </div>
            <p className="text-blue-100 text-sm mb-4">
              Profesionální elektrikářské služby pro Rychnov nad Kněžnou, Náchod, Hradec Králové a okolí
            </p>
            <div className="flex items-center justify-center gap-6 text-sm">
              <a href="tel:775280542" className="hover:text-blue-200 transition-colors">
                775 280 542
              </a>
              <span>|</span>
              <a href="mailto:cejpa.ivo@seznam.cz" className="hover:text-blue-200 transition-colors">
                cejpa.ivo@seznam.cz
              </a>
            </div>
            <p className="text-blue-200 text-xs mt-6">
              © {new Date().getFullYear()} ElektroIvo - Ivo Čejpa. Všechna práva vyhrazena.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
